// Modules
import { produce } from 'immer'
import findIndex from 'lodash/findIndex'

// Constants
import {
  mainTabsList,
  secondaryTabsList,
} from 'pages/Dashboard/components/OrdersBottomBar/HeaderBar/constants'
import {
  ordersBarPositionsLengthDefaultValue,
  ordersBarPositionsLengthKey,
} from 'pages/Dashboard/components/OrdersBottomBar/constants'

// Hooks
import { paginationDefault } from 'hooks/useOrderBottomPagination'

// Types
import {
  GET_CLOSED_TRANSACTIONS_LIST,
  GET_OPEN_TRANSACTIONS_LIST,
  GET_PENDING_TRANSACTIONS_LIST,
  GET_USER_TRANSACTIONS_LIST,
  SET_CLOSED_TOTAL_PROFIT,
  SET_CLOSED_TOTAL_VOLUME,
  SET_CLOSED_TRANSACTIONS_LIST,
  SET_CLOSED_TRANSACTIONS_LIST_LOADING,
  SET_CLOSED_TRANSACTIONS_LIST_PAGINATION,
  SET_IS_FULL_SIZE_MODAL,
  SET_MAIN_TABS_VALUE,
  SET_OPEN_TRANSACTIONS_LIST,
  SET_OPEN_TRANSACTIONS_LIST_LOADING,
  SET_OPEN_TRANSACTIONS_LIST_PAGINATION,
  SET_PENDING_ORDERS_LIST,
  SET_PENDING_ORDERS_LIST_PAGINATION,
  SET_PENDING_TRANSACTIONS_LIST_LOADING,
  SET_POSITIONS_LENGTH,
  SET_SEARCH_VALUE,
  SET_SECONDARY_TABS_VALUE,
  SET_SELECT_TIME_VALUE,
  SET_USER_TRANSACTIONS_LIST,
  SET_USER_TRANSACTIONS_LIST_LOADING,
  TRANSACTION_CONTENT_BAR_LOADING,
  WS_FOLLOW_PROFITS_CHANGE_VALUE,
} from 'store/actions/OrderBar/types'

const initialState = {
  positionsList: null,
  positionsListPagination: paginationDefault,

  closedPositionsList: null,
  closedPositionsListPagination: paginationDefault,
  closedTotalProfit: 0,
  closedTotalVolume: 0,

  pendingOrdersList: null,
  pendingOrdersListPagination: paginationDefault,

  mainTabsValue: mainTabsList?.[0]?.value,
  secondaryTabsValue: secondaryTabsList?.[0]?.value,
  searchValue: '',
  selectTimeValue: '',
  isFullSizeModal: false,

  userTransactionsList: null,
  userTransactionsListLoading: false,
  positionsLength:
    JSON.parse(localStorage.getItem(ordersBarPositionsLengthKey)) ||
    ordersBarPositionsLengthDefaultValue,

  openListLoading: false,
  closedListLoading: false,
  pendingListLoading: false,

  loadingBar: true,
}

const orderBarReducer = (state = initialState, action) => {
  switch (action.type) {
    case SET_MAIN_TABS_VALUE:
      return produce(state, (draft) => {
        draft.mainTabsValue = action.payload
      })

    case SET_SECONDARY_TABS_VALUE:
      return produce(state, (draft) => {
        draft.secondaryTabsValue = action.payload
      })

    case SET_SEARCH_VALUE:
      return produce(state, (draft) => {
        draft.searchValue = action.payload
      })

    case SET_SELECT_TIME_VALUE:
      return produce(state, (draft) => {
        draft.selectTimeValue = action.payload
      })

    case SET_IS_FULL_SIZE_MODAL:
      return produce(state, (draft) => {
        draft.isFullSizeModal = action.payload
      })

    case SET_USER_TRANSACTIONS_LIST:
      return produce(state, (draft) => {
        draft.userTransactionsList = action.payload
      })

    case GET_USER_TRANSACTIONS_LIST:
      return produce(state, (draft) => {
        draft.userTransactionsListLoading = true
      })

    case SET_USER_TRANSACTIONS_LIST_LOADING:
      return produce(state, (draft) => {
        draft.userTransactionsListLoading = action.payload
      })

    case SET_POSITIONS_LENGTH:
      return produce(state, (draft) => {
        draft.positionsLength = action.payload
      })

    // open
    case SET_OPEN_TRANSACTIONS_LIST:
      return produce(state, (draft) => {
        draft.positionsList = action.payload
      })

    case SET_OPEN_TRANSACTIONS_LIST_PAGINATION:
      return produce(state, (draft) => {
        draft.positionsListPagination = { ...draft.positionsListPagination, ...action.payload }
      })

    case GET_OPEN_TRANSACTIONS_LIST:
      return produce(state, (draft) => {
        draft.openListLoading = true
      })

    case SET_OPEN_TRANSACTIONS_LIST_LOADING:
      return produce(state, (draft) => {
        draft.openListLoading = action.payload
      })

    // pending
    case SET_PENDING_ORDERS_LIST:
      return produce(state, (draft) => {
        draft.pendingOrdersList = action.payload
      })

    case SET_PENDING_ORDERS_LIST_PAGINATION:
      return produce(state, (draft) => {
        draft.pendingOrdersListPagination = {
          ...draft.pendingOrdersListPagination,
          ...action.payload,
        }
      })

    case GET_PENDING_TRANSACTIONS_LIST:
      return produce(state, (draft) => {
        draft.pendingListLoading = true
      })

    case SET_PENDING_TRANSACTIONS_LIST_LOADING:
      return produce(state, (draft) => {
        draft.pendingListLoading = action.payload
      })

    // closed
    case SET_CLOSED_TRANSACTIONS_LIST:
      return produce(state, (draft) => {
        draft.closedPositionsList = action.payload
      })

    case SET_CLOSED_TRANSACTIONS_LIST_PAGINATION:
      return produce(state, (draft) => {
        draft.closedPositionsListPagination = {
          ...draft.closedPositionsListPagination,
          ...action.payload,
        }
      })

    case GET_CLOSED_TRANSACTIONS_LIST:
      return produce(state, (draft) => {
        draft.closedListLoading = true
      })

    case SET_CLOSED_TRANSACTIONS_LIST_LOADING:
      return produce(state, (draft) => {
        draft.closedListLoading = action.payload
      })

    case SET_CLOSED_TOTAL_PROFIT:
      return produce(state, (draft) => {
        draft.closedTotalProfit = action.payload
      })

    case SET_CLOSED_TOTAL_VOLUME:
      return produce(state, (draft) => {
        draft.closedTotalVolume = action.payload
      })

    case TRANSACTION_CONTENT_BAR_LOADING:
      return produce(state, (draft) => {
        draft.loadingBar = action.payload
      })

    // profit socket
    case WS_FOLLOW_PROFITS_CHANGE_VALUE:
      const tradeId = action.payload?.tradeId

      return produce(state, (draft) => {
        const itemOpenIndexToChange = findIndex(draft.positionsList, { id: tradeId })
        const itemPendingIndexToChange = findIndex(draft.pendingOrdersList, { id: tradeId })

        if (itemOpenIndexToChange !== -1) {
          draft.positionsList[itemOpenIndexToChange] = {
            ...draft.positionsList[itemOpenIndexToChange],
            current_rate: action.payload?.currentRate,
            profit: action.payload?.profit,
          }
        }

        if (itemPendingIndexToChange !== -1) {
          draft.pendingOrdersList[itemPendingIndexToChange] = {
            ...draft.pendingOrdersList[itemPendingIndexToChange],
            current_rate: action.payload?.currentRate,
            profit: action.payload?.profit,
          }
        }
      })

    default:
      return state
  }
}
export default orderBarReducer
